import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../_shared/models';
import { DateInterval } from '../../../_shared/enums';
import { FilterService } from '../../../_shared/services/filter.service';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { MonthlyCostComparisonComponent } from './components/graphs/monthly-cost-comparison/monthly-cost-comparison.component';
import { CpkTrendComponent } from './components/graphs/cpk-trend/cpk-trend.component';
import { HighestCostByContractTypeComponent } from './components/graphs/highest-cost-by-contract-type/highest-cost-by-contract-type.component';
import { MaintenanceCostingTabType } from '../../_shared/models/tabs.models';
import { OutsourcedMaintenanceContractComponent } from './components/outsourced-maintenance-contract/outsourced-maintenance-contract.component';
import { InHouseMaintenanceComponent } from './components/in-house-maintenance/in-house-maintenance.component';
import { AncillaryCostComponent } from './components/ancillary-cost/ancillary-cost.component';
import { FullViewService } from './services/full-view.service';
import { MaintenanceContractColumnService } from './services/maintenance-contract-column.service';
import { CustomizeColumnModalComponent } from './components/modals/customize-column-modal/customize-column-modal.component';
import { AddContractModalComponent } from './components/modals/add-contract-modal/add-contract-modal.component';
import { AddInvoiceModalComponent } from './components/modals/add-invoice-modal/add-invoice-modal.component';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-costing',
  standalone: true,
  imports: [
    CommonModule,
    AccountFilterComponent,
    DateFilterComponent,
    MonthlyCostComparisonComponent,
    CpkTrendComponent,
    HighestCostByContractTypeComponent,
    OutsourcedMaintenanceContractComponent,
    InHouseMaintenanceComponent,
    AncillaryCostComponent,
    TranslocoDirective
  ],
  templateUrl: './maintenance-costing.component.html',
  styleUrl: './maintenance-costing.component.scss'
})
export class MaintenanceCostingComponent extends BasePageComponent {
  readonly fullViewService: FullViewService = inject(FullViewService);
  private filterService: FilterService = inject(FilterService);
  private columnService: MaintenanceContractColumnService = inject(MaintenanceContractColumnService);
  private modalService = inject(NgbModal);

  @ViewChild(AncillaryCostComponent) private ancillaryCostTable?: AncillaryCostComponent;

  isLoading: boolean = false;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  selectedTab: MaintenanceCostingTabType = 'outsourced-maintenance';
  selectedColumns: string[];

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    // The user's saved selection when they have one, otherwise the default set
    this.selectedColumns = this.columnService.getSelectedColumns();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);
      this.updatePageFilterState();
    }
  }

  onCustomiseColumn(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(CustomizeColumnModalComponent, modalOptions);

    modalRef.result.then(
      (selectedColumns: string[]) => {
        // The modal has already stored the selection; handing it down re-renders the table
        this.selectedColumns = selectedColumns;
      },
      () => {
      }
    );
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    // The service outlives the page, so leaving in full view would reopen the page without its graphs
    this.fullViewService.disableFullView();
  }

  onAddContract(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(AddContractModalComponent, modalOptions);

    modalRef.result.then(
      () => {
        // The modal saves the contract itself; re-publishing the filter reloads the contract list and
        // the outsourced and contract-type graphs, which all include the new contract's costs
        this.updateFilter();
      },
      () => {
        // Modal dismissed
      }
    );
  }

  onAddInvoice(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(AddInvoiceModalComponent, modalOptions);

    modalRef.result.then(
      () => {
        // Ancillary costs feed none of the graphs, so only the list needs the row that was just added
        this.ancillaryCostTable?.reload();
      },
      () => {
        // Modal dismissed
      }
    );
  }

  onTabChange(tab: MaintenanceCostingTabType): void {
    this.selectedTab = tab;
  }

  isTabActive(tab: MaintenanceCostingTabType): boolean {
    return this.selectedTab === tab;
  }

  onBackToMainView(): void {
    // The active table reloads its preview page itself and the re-created graphs load on init
    this.fullViewService.disableFullView();
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter
    }
  }

  protected override applyPageFilterState(pageFilter: any): void { }
}